/**
 * Debounce Utility
 *
 * Delays the execution of a function until after a specified wait time
 * has elapsed since the last time it was invoked.
 * Useful for preventing rapid consecutive calls (e.g., rapid language switching).
 */

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was invoked.
 *
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the function
 *
 * @example
 * ```ts
 * const debouncedSearch = debounce((query: string) => {
 *   console.log('Searching for:', query);
 * }, 300);
 *
 * debouncedSearch('test'); // Will not execute immediately
 * debouncedSearch('test2'); // Cancels previous call
 * debouncedSearch('test3'); // Will execute after 300ms
 * ```
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;

  return function debounced(...args: Parameters<T>) {
    // Clear any existing timeout
    if (timeout) {
      clearTimeout(timeout);
    }

    // Set new timeout
    timeout = setTimeout(() => {
      func(...args);
      timeout = null;
    }, wait);
  };
}

/**
 * Creates a debounced async function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was invoked.
 * Returns a promise that resolves when the debounced function executes.
 *
 * @param func - The async function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns A debounced version of the async function
 *
 * @example
 * ```ts
 * const debouncedFetch = debounceAsync(async (url: string) => {
 *   const response = await fetch(url);
 *   return response.json();
 * }, 500);
 *
 * debouncedFetch('/api/data'); // Returns a promise
 * ```
 */
export function debounceAsync<T extends (...args: any[]) => Promise<any>>(
  func: T,
  wait: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
  let timeout: NodeJS.Timeout | null = null;
  let resolveQueue: Array<(value: any) => void> = [];
  let rejectQueue: Array<(error: any) => void> = [];

  return function debouncedAsync(...args: Parameters<T>): Promise<ReturnType<T>> {
    return new Promise((resolve, reject) => {
      // Clear any existing timeout
      if (timeout) {
        clearTimeout(timeout);
      }

      // Add resolve/reject to queue
      resolveQueue.push(resolve);
      rejectQueue.push(reject);

      // Set new timeout
      timeout = setTimeout(async () => {
        try {
          const result = await func(...args);

          // Resolve all queued promises
          resolveQueue.forEach((res) => res(result));
          resolveQueue = [];
          rejectQueue = [];
        } catch (error) {
          // Reject all queued promises
          rejectQueue.forEach((rej) => rej(error));
          resolveQueue = [];
          rejectQueue = [];
        }

        timeout = null;
      }, wait);
    });
  };
}

/**
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds.
 *
 * @param func - The function to throttle
 * @param wait - The number of milliseconds to throttle invocations to
 * @returns A throttled version of the function
 *
 * @example
 * ```ts
 * const throttledScroll = throttle(() => {
 *   console.log('Scroll event');
 * }, 100);
 *
 * window.addEventListener('scroll', throttledScroll);
 * ```
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let lastCall = 0;

  return function throttled(...args: Parameters<T>) {
    const now = Date.now();

    if (now - lastCall >= wait) {
      lastCall = now;
      func(...args);
    }
  };
}
